import ContentContainer from '@/features/shared/ContentContainer';
import DetailContent from '@/features/shared/DetailContent';
import { MuiContainer, MuiTab, MuiTabs, MuiTypography } from '@/features/shared/material-ui';
import RichText, { CaptureClicksRichText } from '@/features/shared/RichText';
import { InsetTop, spacingSizesMap, Theme } from '@/utils/constants';
import { isAnchorSectionLink } from '@/utils/helpers';
import { useMediaQuery } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import clsx from 'clsx';
import React, {
  ChangeEvent,
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import TagManager from 'react-gtm-module';
import VisibilitySensor from 'react-visibility-sensor';
import { VerticalContentProps } from './VerticalContentFeatured.interface';
import useStyles from './VerticalContentFeatured.styles';

const mapVariantToProps = ({ variant }: any) => {
  if (/^image$/.test(variant)) {
    return {
      type: 'featured',
    };
  }

  return {};
};

const mapVariantToDetailContentClassName =
  (classes: any) =>
  ({ variant }: any) => {
    if (/^rtc$/.test(variant)) {
      return classes.rtc;
    }

    if (/^link$/.test(variant)) {
      return classes.link;
    }

    return '';
  };

const Featured = ({ tabDetailContent, classes, theme }: any) => {
  const { heading, detailContent } = tabDetailContent;

  const featuredClasses = clsx('featured-tab gtm-tab', classes.featured);
  return (
    <div className={featuredClasses}>
      {heading && (
        <RichText
          customStyles
          theme={theme}
          className={clsx(classes.heading, 'gtm-headline')}
          content={heading}
        />
      )}
      <DetailContent
        content={detailContent}
        theme={theme}
        mapVariantToClassName={mapVariantToDetailContentClassName(classes)}
      />
    </div>
  );
};

const TabDescription = ({ theme, description, classes }: any) => {
  const scrollTo = (event: any) => {
    const target = event.target || null;
    const nodeType = target && target.nodeName;
    const anchorId = target && target.getAttribute('data-anchor-id');

    if (nodeType === 'A' && isAnchorSectionLink(anchorId)) {
      event.preventDefault();
      const identifier = anchorId.replace(/^#/, '');
      const anchorElm = document.querySelector(`#${identifier}`) as Element;
      anchorElm?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  if (!description) return null;

  return (
    <CaptureClicksRichText
      customStyles
      content={description}
      theme={theme}
      className={classes.tabDescription}
      onClick={scrollTo}
    />
  );
};

const ScrollList = ({
  tabs,
  classes,
  value,
  theme,
  callback,
  topSpacing,
}: {
  tabs: TabContentType[];
  classes: any;
  value: number;
  theme: ThemeType;
  callback: any;
  topSpacing: any;
}) => {
  const compRef = useRef<HTMLDivElement>(null);

  const handleScroll = useCallback(() => {
    if (!compRef?.current) return;

    const elRefs = [...compRef.current.querySelectorAll('[data-number]')] as HTMLDivElement[];
    const CENTER_Y = window.innerHeight / 2;

    const centerEl = elRefs.find((item: any) => {
      const { top, height } = item.getBoundingClientRect();
      return CENTER_Y >= top && CENTER_Y < top + height;
    });

    const callbackValue = Number(centerEl?.dataset?.number);

    if (!Number.isNaN(callbackValue)) {
      callback(callbackValue);
    }
  }, [compRef, callback]);

  const handleVisibilitySensorChange = useCallback((isVisible: boolean) => {
    if (isVisible) {
      window.addEventListener('scroll', handleScroll);
    } else {
      window.removeEventListener('scroll', handleScroll);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <VisibilitySensor active delayedCall partialVisibility onChange={handleVisibilitySensorChange}>
      <div className={classes.scrollable} ref={compRef}>
        {tabs.map(({ tabTitle, tabDescription, featuredContent, tabDetailContent }, i) => {
          const shouldHaveTopPadding = (topSpacing && i === 1) || undefined;

          return (
            <div
              className={
                shouldHaveTopPadding && `ctl-vertical-content-featured top-${topSpacing}-md`
              }
              data-number={i}
              // eslint-disable-next-line react/no-array-index-key
              key={`elm_${i}`}
              // The first element is displayed differently, so it needs a "-1" index adjustment.
              data-datalayer-scrollcarousel-index={i}
              data-datalayer-scrollcarousel-title={tabTitle?.text}
            >
              {i === 0 && featuredContent ? (
                <Featured
                  tabDetailContent={tabDetailContent}
                  tabDescription={tabDescription}
                  classes={classes}
                  theme={theme}
                />
              ) : (
                <div
                  className={clsx(classes.tabScrollable, 'gtm-tab')}
                  aria-selected={value === i ? 'true' : 'false'}
                >
                  <div className="tab-content">
                    <MuiTypography className="heading gtm-headline" variant="body1" component="h3">
                      {tabTitle?.text}
                    </MuiTypography>
                    <TabDescription description={tabDescription} theme={theme} classes={classes} />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </VisibilitySensor>
  );
};

const a11yProps = (index: any, isMobile: boolean) => {
  return {
    id: `vertical-featured-tab-${index}`,
    'aria-controls': [
      `vertical-featured-tabpanel-${index}`,
      isMobile ? `vertical-featured-mobiletabpanel-${index}` : '',
    ]
      .join(' ')
      .trim(),
  };
};

interface TabPanelProps {
  children?: any;
  index?: any;
  value?: any;
  panelName?: string;
  classes?: any;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, panelName, classes, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`vertical-${panelName}-${index}`}
      aria-labelledby={`vertical-tab-${index}`}
      className={classes.tabPanel}
      {...other}
    >
      {children}
    </div>
  );
};

const DetailContentPanels = ({
  classes,
  tabs = [],
  value = 0,
  theme,
}: {
  classes: any;
  tabs: TabContentType[];
  value: number;
  theme: ThemeType;
}) => {
  return (
    <>
      {tabs.map(({ tabDetailContent }, i) => {
        const { mediaContent } = tabDetailContent || {};

        return (
          <TabPanel
            // eslint-disable-next-line react/no-array-index-key
            key={`tabPanel_${i}`}
            value={value}
            panelName="tabpanel"
            index={i}
            classes={classes}
          >
            <DetailContent
              content={mediaContent}
              mapVariantToProps={mapVariantToProps}
              theme={theme}
            />
          </TabPanel>
        );
      })}
    </>
  );
};

const MobileHeadingPanels = ({
  tabs,
  value,
  theme,
  classes,
}: {
  tabs: TabContentType[];
  value: number;
  theme: ThemeType;
  classes: any;
}) => (
  <>
    {tabs.map(({ tabDescription, tabTitle }, i) => (
      <TabPanel
        // eslint-disable-next-line react/no-array-index-key
        key={`tabPanel_${i}`}
        value={value}
        panelName="mobiletabpanel"
        index={i}
        classes={classes}
        data-datalayer-scrollcarousel-index={i}
        data-datalayer-scrollcarousel-title={tabTitle?.text}
      >
        <TabDescription description={tabDescription} theme={theme} classes={classes} />
      </TabPanel>
    ))}
  </>
);

const CustomTabComponent = forwardRef((props: any, ref: any) => {
  const { children, tabDescription, ...tabProps } = props;
  return (
    <div {...tabProps} ref={ref}>
      <MuiTypography className="heading gtm-headline" variant="body1" component="h3">
        {children}
      </MuiTypography>
      {tabDescription}
    </div>
  );
});

const VerticalContentFeatured = ({
  theme = Theme.light,
  tabGroupTitle,
  tabs = [],
  containerProps,
  contentButtons,
}: Br.PropsWithContentButtons<VerticalContentProps>) => {
  const classes = useStyles({});
  const verticalContentRef = useRef<HTMLDivElement>(null);
  const [value, setValue] = useState<number>();
  const [sensorIndex, setSensorIndex] = useState<number[]>([]);
  // const [topHeight, setTopHeight] = useState<any>(0);
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const featuredIndex = useMemo(() => tabs.findIndex((item) => item.featuredContent), [tabs]);
  const rootRef = useRef<HTMLDivElement>(null);
  const elements = useMemo(() => {
    if (featuredIndex > -1) {
      const featured = tabs[featuredIndex];
      return [featured, ...tabs.filter((item, id) => id !== featuredIndex)];
    }

    return JSON.parse(JSON.stringify(tabs)) as TabContentType[];
  }, [tabs, featuredIndex]);

  useEffect(() => {
    if (value === undefined) return;

    const validateIfWasScrolled = sensorIndex.some((x: any) => x === value);
    if (!validateIfWasScrolled) {
      setSensorIndex((prev) => [...prev, value]);
      // const indexNum = value > 0 ? value - 1 : value;
      const dataLayer = {
        event: 'trackEvent-scrollCarouselNavScroll',
        event_name: 'scroll_carousel_nav_scroll',
        title: tabs[value]?.tabTitle?.text,
        index_num: String(value),
      };
      TagManager.dataLayer({ dataLayer });
    }
  }, [sensorIndex, tabs, value]);

  const getFeaturedTabHeight = useCallback(() => {
    if (!verticalContentRef?.current) return 0;

    const superheadingEl = verticalContentRef?.current?.querySelector(
      '.featured-title',
    ) as HTMLElement;
    const featuredTabEl = verticalContentRef?.current?.querySelector(
      '.featured-tab',
    ) as HTMLElement;

    const superheadingElHeight = superheadingEl?.clientHeight
      ? superheadingEl.clientHeight + 16
      : 0;

    const featuredTabHeight = featuredTabEl?.clientHeight
      ? spacingSizesMap.medium + featuredTabEl?.clientHeight
      : 0;

    const topBgHeightNumber = featuredTabHeight + superheadingElHeight;

    return topBgHeightNumber || 0;
  }, [verticalContentRef]);

  useEffect(() => {
    const updateTopHeight = () => {
      const topHeight = !isMobile ? getFeaturedTabHeight() : 0;
      const topBgEl = verticalContentRef?.current?.querySelector('.top-background') as HTMLElement;
      topBgEl.style.paddingBottom = topHeight ? `${topHeight}px` : '';
    };

    updateTopHeight();

    const handleResize = () => {
      updateTopHeight();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [getFeaturedTabHeight, isMobile]);

  // eslint-disable-next-line @typescript-eslint/ban-types
  const handleChange = (event: ChangeEvent<{}>, newValue: number) => {
    setValue(newValue);
    const dataLayer = {
      event: 'trackEvent-scrollCarouselNavClick',
      event_name: 'scroll_carousel_nav_click',
      title: tabs[newValue]?.tabTitle?.text,
      index_num: String(newValue),
    };
    TagManager.dataLayer({ dataLayer });
  };

  useEffect(() => {
    const tabTitle = elements[value || 0]?.tabTitle?.text || '';

    rootRef?.current?.dispatchEvent(
      new CustomEvent('gtm:scroll', {
        bubbles: true,
        detail: { target: { tabTitle } },
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const setStickyTop = useCallback(
    (el: HTMLElement | null) => {
      if (el) {
        const top = el?.getBoundingClientRect().height / 2;
        // eslint-disable-next-line no-param-reassign
        el.style.top = isMobile ? '' : `calc(50vh - ${top}px)`;
      }
    },
    [isMobile],
  );

  const rootClasses = clsx(classes.root, `${theme}-theme`);
  const titleClasses = clsx('featured-title', classes.title);

  return (
    <div ref={verticalContentRef}>
      <ContentContainer
        {...containerProps}
        mobileTopBackgroundColor={containerProps?.topBackgroundColor}
        mobileBottomBackgroundColor={containerProps?.bottomBackgroundColor}
        topSpacing={containerProps?.topSpacing}
        bottomSpacing={containerProps?.bottomSpacing}
        insetTop={InsetTop.video}
        data-component-variant="vertical-content-featured"
      >
        <MuiContainer className="large">
          <div className={rootClasses} ref={rootRef}>
            <div className={classes.content}>
              <div className="tabPanels vertical-featured-sticky" ref={(el) => setStickyTop(el)}>
                <DetailContentPanels
                  tabs={elements}
                  value={value || 0}
                  classes={classes}
                  theme={theme}
                />
              </div>
              <div className="tabs">
                {contentButtons?.content}

                {tabGroupTitle && (
                  <MuiTypography variant="overline" component="h3" className={titleClasses}>
                    {tabGroupTitle}
                  </MuiTypography>
                )}

                {isMobile ? (
                  <>
                    <MuiTabs
                      orientation="horizontal"
                      variant="scrollable"
                      scrollButtons="on"
                      value={value}
                      onChange={handleChange}
                      aria-label="Horizontal tabs"
                      className={classes.tabs}
                    >
                      {elements.map(({ tabTitle }, i) => (
                        <MuiTab
                          // eslint-disable-next-line react/no-array-index-key
                          key={`tab_${i}`}
                          label={tabTitle?.text}
                          {...a11yProps(i, isMobile)}
                          component={CustomTabComponent}
                          className={clsx(classes.tab, 'gtm-tab')}
                        />
                      ))}
                    </MuiTabs>
                    <MobileHeadingPanels
                      theme={theme}
                      tabs={elements}
                      value={value || 0}
                      classes={classes}
                    />
                  </>
                ) : (
                  <>
                    <ScrollList
                      topSpacing={containerProps?.topSpacing}
                      tabs={elements}
                      classes={classes}
                      callback={(num: number) => setValue(num)}
                      value={value || 0}
                      theme={theme}
                    />
                  </>
                )}
              </div>
            </div>
          </div>
        </MuiContainer>
      </ContentContainer>
    </div>
  );
};

export default memo(VerticalContentFeatured);
